/* eslint-disable array-bracket-newline */
/**
 * Implements token list and token distribution
 */
import { AutoSizer, List } from 'react-virtualized';
import ReactTooltip from 'react-tooltip';
import { IWalletOverviewToken } from '@kattana-io/common';
import Text from '../../../atoms/Text';
import TokenItem from './TokenItem';
import TokensPlaceholder from './Placeholder';

import './styles.scss';

const TOKENS_DISTRIBUTION_LIMIT = 5;

export const getTokensStatistics = (data: IWalletOverviewToken[]) => {
  const tokens = data
    .map((item) => ({
      balance: item.BalanceUSD,
      label: item.TokenSymbol || item.TokenName,
      key: `${item.Address}-${item.TokenSymbol}-${item.TokenName}`,
    }))
    .filter((item) => item.balance);
  tokens.sort((a, b) => b.balance - a.balance);
  const tokensTotal = tokens.reduce((cons, item) => cons + item.balance, 0);
  const tokensStatistics = tokens
    .slice(0, TOKENS_DISTRIBUTION_LIMIT)
    .map((item) => ({
      label: item.label,
      value: tokensTotal ? (item.balance / tokensTotal) * 100 : 0,
      key: item.key,
    }));

  if (tokens.length > TOKENS_DISTRIBUTION_LIMIT) {
    const totalOthers = tokens
      .slice(TOKENS_DISTRIBUTION_LIMIT)
      .reduce((cons, item) => cons + item.balance, 0);
    return {
      tokens: [
        ...tokensStatistics,
        {
          label: 'Others',
          value: (totalOthers / tokensTotal) * 100,
          key: 'others',
        },
      ],
      size: 12,
    };
  }
  if (tokensStatistics.length > 2) {
    return { tokens: tokensStatistics, size: 14 };
  }
  return { tokens: tokensTotal === 0 ? [] : tokensStatistics, size: 24 };
};

interface ITokensProps {
  tokens: IWalletOverviewToken[];
  loading: boolean;
  chain?: string;
}

const Tokens = ({ tokens, loading, chain }: ITokensProps) => {
  const tokensStatics = getTokensStatistics(tokens);

  if (loading) {
    return <TokensPlaceholder />;
  }

  const rowRenderer = ({
    key, // Unique key within array of rows
    index, // Index of row within collection
    style,
  }: any) => {
    return (
      <TokenItem
        key={key}
        token={tokens[index]}
        style={style}
        chain={chain}
        disabled={!index}
      />
    );
  };

  return (
    <div className='tokens'>
      <div className='tokens__list'>
        <Text size={16} className='tokens__list__title'>
          Tokens
        </Text>
        <AutoSizer>
          {({ width }) => (
            <List
              width={width}
              height={166}
              rowCount={tokens?.length || 0}
              rowHeight={34}
              rowRenderer={rowRenderer}
              className='tokens__list__items'
            />
          )}
        </AutoSizer>
      </div>
      <div className='tokens__distribution'>
        <div className='tokens__distribution__header'>
          <div className='tokens__distribution__header__subtitle'>
            <Text size={12} className='text--grey-400'>
              Distribution
            </Text>
            <div
              className='tokens__distribution__header__subtitle__icon icon-alert-circle'
              data-for='account-overview-tooltip'
              data-tip='Spreading digital assets among<br>users through transactions, rewards,<br>or methods within a blockchain network.'
            />
            <ReactTooltip
              id='account-overview-tooltip'
              type='light'
              place='bottom'
              effect='solid'
              className='text_tooltip'
              multiline={true}
            />
          </div>
          <Text size={12} className='text--grey-600'>
            Analyze the distribution of tokens on the selected wallet
          </Text>
        </div>
        <div className='tokens__distribution__body'>
          {tokensStatics.tokens.length ? (
            <>
              <div className='tokens__distribution__body__comparison'>
                {tokensStatics.tokens.map((item) => (
                  <div
                    key={item.key}
                    className='tokens__distribution__body__comparison__item'
                    style={{ width: `${item.value}%` }}
                  >
                    <Text
                      size={tokensStatics.size}
                      style={{
                        title: `${item.value}%`,
                      }}
                    >
                      {item.value.toFixed(1)}%
                    </Text>
                  </div>
                ))}
              </div>
              <div className='tokens__distribution__body__schema'>
                {tokensStatics.tokens.map((item) => (
                  <div
                    key={item.key}
                    className='tokens__distribution__body__schema__item'
                  >
                    <div />
                    <Text size={12}>{item.label}</Text>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className='centrify' style={{ height: 300 }}>
              <Text size={14} className='text--grey-200'>
                We couldn&apos;t find any tokens in selected wallet...
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tokens;
